import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, User, Coffee, Moon, Sun, Heart, Sparkles, Gift } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useContent } from '../context/ContentContext';
import { Button } from './ui/button';

// Default logo URL - can be overridden via environment variable for production
const DEFAULT_LOGO = "/images/bulaccino-logo-white.png";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const { isDarkMode, toggleDarkMode, darkModeEnabled } = useTheme();
  const { getNavigation } = useContent();
  const location = useLocation();

  // Get navigation data from CMS
  const navigation = getNavigation();
  
  // Extract branding and nav links with fallbacks
  const branding = navigation.branding || {
    logo: DEFAULT_LOGO,
    siteName: "Bulaccino Cafe",
    showSiteName: false
  };
  
  const navLinksData = navigation.navLinks || {
    links: [
      { label: 'Home', url: '/' },
      { label: 'Menu', url: '/menu' },
      { label: 'Build Your Own', url: '/build-your-own' },
      { label: 'Coffee Pass', url: '/bula-coffee-club', isSpecial: true },
      { label: 'Rewards', url: '/loyalty' },
      { label: 'About', url: '/about' },
      { label: 'Contact', url: '/contact' },
    ]
  };
  
  const ctaData = navigation.cta || {
    button: { text: 'Order Now', link: '/order', icon: 'coffee' }
  };

  // Use env variable for logo or CMS value
  const logoUrl = process.env.REACT_APP_LOGO_URL || branding.logo || DEFAULT_LOGO;

  // Transform CMS links format to expected format
  const navLinks = navLinksData.links.map(link => ({
    path: link.url,
    label: link.label,
    isSpecial: link.isSpecial || false
  }));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      <header 
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'header-scrolled py-3' : 'bg-transparent py-5'
        }`}
        style={{ top: 'var(--banner-height, 0px)' }}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <nav className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src={logoUrl} 
                alt={branding.siteName || "Bulaccino Cafe"} 
                className="h-14 w-auto dark:drop-shadow-[0_0_10px_rgba(255,255,255,1)] dark:filter"
              />
              {branding.showSiteName && (
                <span className="font-heading text-xl">{branding.siteName}</span>
              )}
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map(link => (
                link.isSpecial ? (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid="nav-coffee-pass"
                    className={`flex items-center gap-1.5 text-sm font-medium transition-colors hover:text-primary ${
                      location.pathname === link.path 
                        ? 'text-primary' 
                        : 'text-foreground/80'
                    }`}
                  >
                    <Coffee className="w-4 h-4" />
                    {link.label}
                    <span className="px-1.5 py-0.5 bg-accent text-white text-[10px] font-bold rounded-full leading-none">
                      NEW
                    </span>
                  </Link>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`text-sm font-medium transition-colors hover:text-primary ${
                      location.pathname === link.path 
                        ? 'text-primary' 
                        : 'text-foreground/80'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {/* Dark Mode Toggle */}
              {darkModeEnabled && (
                <button
                  onClick={toggleDarkMode}
                  className="p-2 hover:bg-muted rounded-full transition-colors"
                  data-testid="dark-mode-toggle"
                  aria-label="Toggle dark mode"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5" />
                  ) : (
                    <Moon className="w-5 h-5" />
                  )}
                </button>
              )}

              {/* Order Button (CTA) */}
              <Link to={ctaData.button.link || "/order"} data-testid="nav-order-btn">
                <Button 
                  className="hidden sm:flex rounded-full bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20"
                >
                  <Coffee className="w-4 h-4 mr-2" />
                  {ctaData.button.text || "Order Now"}
                </Button>
              </Link>

              {/* Cart */}
              <Link 
                to="/order" 
                className="relative p-2 hover:bg-muted rounded-full transition-colors"
                data-testid="cart-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white text-xs rounded-full flex items-center justify-center cart-badge">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* User */}
              {isAuthenticated ? (
                <div className="hidden sm:flex items-center gap-2">
                  <Link 
                    to="/favorites" 
                    className="p-2 hover:bg-muted rounded-full transition-colors"
                    data-testid="favorites-nav-btn"
                  >
                    <Heart className="w-5 h-5" />
                  </Link>
                  <Link 
                    to="/profile" 
                    className="flex items-center gap-2 p-2 hover:bg-muted rounded-full transition-colors"
                    data-testid="user-menu-btn"
                  >
                    <User className="w-5 h-5" />
                    <span className="text-sm font-medium">{user?.name?.split(' ')[0]}</span>
                  </Link>
                  <button 
                    onClick={logout}
                    className="text-sm text-muted-foreground hover:text-foreground"
                    data-testid="logout-btn"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <Link 
                  to="/my-account"
                  className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-300 text-foreground relative group hover:bg-primary/10 hover:scale-105"
                  data-testid="my-rewards-btn"
                >
                  {/* Animated glow ring */}
                  <span className="absolute inset-0 rounded-full bg-gradient-to-r from-primary/20 to-amber-500/20 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Icon with bounce animation */}
                  <Gift className="w-5 h-5 text-primary animate-bounce-subtle relative z-10" />
                  <span className="text-sm font-medium relative z-10">My Rewards</span>
                  
                  {/* NEW badge */}
                  <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full shadow-sm animate-pulse">
                    NEW
                  </span>
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 hover:bg-muted rounded-full transition-colors"
                data-testid="mobile-menu-btn"
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </nav>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-lg border-b border-border shadow-lg">
              <div className="px-6 py-6 space-y-4">
                {navLinks.map(link => (
                  link.isSpecial ? (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid="mobile-nav-coffee-pass"
                      className={`flex items-center gap-2 py-2 text-lg font-medium ${
                        location.pathname === link.path 
                          ? 'text-primary' 
                          : 'text-foreground/80'
                      }`}
                    >
                      <Coffee className="w-5 h-5" />
                      {link.label}
                      <span className="px-2 py-0.5 bg-accent text-white text-xs font-bold rounded-full">
                        NEW
                      </span>
                    </Link>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`block py-2 text-lg font-medium transition-colors ${
                        location.pathname === link.path 
                          ? 'text-primary' 
                          : 'text-foreground/80'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )
                ))}
                
                {/* My Rewards - Always visible for all visitors */}
                <Link 
                  to="/my-account"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2 py-3 text-lg font-semibold text-primary border-t border-border mt-2"
                  data-testid="mobile-my-rewards-main-btn"
                >
                  <Gift className="w-5 h-5 animate-bounce-subtle" />
                  <span>My Rewards</span>
                  <span className="px-2 py-0.5 text-[10px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full ml-auto">
                    NEW
                  </span>
                </Link>
                
                <div className="pt-4 border-t border-border">
                  {isAuthenticated ? (
                    <div className="space-y-3">
                      <Link 
                        to="/profile"
                        className="flex items-center gap-2 text-foreground"
                        data-testid="mobile-profile-btn"
                      >
                        <User className="w-4 h-4" />
                        <span>{user?.name}</span>
                      </Link>
                      <Link 
                        to="/favorites"
                        className="flex items-center gap-2 text-muted-foreground"
                        data-testid="mobile-favorites-btn"
                      >
                        <Heart className="w-4 h-4" />
                        <span>Favorites</span>
                      </Link>
                      <button 
                        onClick={logout}
                        className="text-sm text-accent hover:underline"
                        data-testid="mobile-logout-btn"
                      >
                        Logout
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
}
